// src/components/AccountSettingsModal.jsx
import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  Box,
  Switch,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Edit as EditIcon,
  DeleteOutline as DeleteOutlineIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { collection, doc, getDocs, updateDoc } from "firebase/firestore";
import { auth, db, storage } from "../@shared/firebase/config";
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  NOTIFICATION_DOC_ID,
  normalizeNotificationPreferences,
} from "../@shared/utils/notificationPreferences";
import { abstractTestnet } from "viem/chains";
import {
  useLoginWithAbstract,
  useGlobalWalletSignerAccount,
} from "@abstract-foundation/agw-react";
import { useAccount, useDisconnect } from "wagmi";
import { MainContext, wagmiConfig } from "../App";
import { verifyInfinityOrbsClient } from "../@shared/utils/verifyInfinityOrbsClient";
import { countryCodes } from "../@shared/utils/countryCodes";
import {
  composeOrbAvatarFromLayers,
  describeOrbSelection,
  getOrbLayerSourceMeta,
  normalizeOrbRecord,
} from "../@shared/utils/orbAvatar";
import ClaimPhysicalCardModal from "./ClaimPhysicalCardModal";
import CharacterCard from "./CharacterCard";
import SwipeInspectModal from "./SwipeInspectModal";
import WalletScanCard from "./WalletScanCard";
import { dialogPaperSx, dialogTitleSx, dialogTitleTextSx } from "../@shared/ui/tokens";

const SHIPPING_ADDRESS_FIELDS = [
  "shippingFullName",
  "shippingLine1",
  "shippingLine2",
  "shippingCity",
  "shippingStateProvince",
  "shippingPostalCode",
];

const trimValue = (value) => String(value || "").trim();

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatClaimedAt = (value) => {
  const millis = toMillis(value);
  if (!millis) return "";
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(millis));
};

const policyLinkSx = {
  color: "#e2d5ff",
  opacity: 0.78,
  display: "block",
  border: 0,
  bgcolor: "transparent",
  p: 0,
  mt: 0.5,
  fontFamily: "inherit",
  textAlign: "left",
  cursor: "pointer",
  "&:hover": {
    color: "#ffd24d",
    opacity: 1,
  },
};

const hasShippingAddressDetails = (form) =>
  SHIPPING_ADDRESS_FIELDS.some((field) => trimValue(form[field]));

const getShippingAddressValidationError = (form) => {
  if (!hasShippingAddressDetails(form)) return "";
  if (!trimValue(form.shippingFullName)) {
    return "Add the recipient's full name to save a shipping address.";
  }
  if (!trimValue(form.shippingLine1)) {
    return "Add an address line 1 to save a shipping address.";
  }
  if (!trimValue(form.shippingCity)) {
    return "Add a city to save a shipping address.";
  }
  if (!trimValue(form.shippingPostalCode)) {
    return "Add a postal / ZIP code to save a shipping address.";
  }
  if (!trimValue(form.country)) {
    return "Select a country to save a shipping address.";
  }
  return "";
};

const AccountSettingsModal = ({ visible, onClose, userData }) => {
  const { activeSection, setActiveSection, navigate } = useContext(MainContext);
  const [form, setForm] = useState({
    displayName: "",
    username: "",
    email: "",
    phone: "",
    xHandle: "",
    abstractWalletAddress: "",
    country: "",
    shippingFullName: "",
    shippingLine1: "",
    shippingLine2: "",
    shippingCity: "",
    shippingStateProvince: "",
    shippingPostalCode: "",
  });

  const [saving, setSaving] = useState(false);
  const [showWalletOptions, setShowWalletOptions] = useState(false);
  const [walletConnecting, setWalletConnecting] = useState(false);
  const [registeredWallet, setRegisteredWallet] = useState(""); // "Default Abstract Wallet"

  const [isUpdateFlow, setIsUpdateFlow] = useState(false);

  // NEW: default wallet confirmation modal state
  const [defaultWalletDialogOpen, setDefaultWalletDialogOpen] = useState(false);
  const [pendingDefaultWallet, setPendingDefaultWallet] = useState("");

  // Notifications
  const [notifications, setNotifications] = useState({
    ...DEFAULT_NOTIFICATION_PREFERENCES,
  });
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  // X handle edit mode
  const [isEditingX, setIsEditingX] = useState(false);

  // Responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isMobileOnList, setIsMobileOnList] = useState(true);

  const [orbImages, setOrbImages] = useState([]);
  const [orbItems, setOrbItems] = useState([]);
  const [orbVerifyMsg, setOrbVerifyMsg] = useState("");
  const [orbActionMsg, setOrbActionMsg] = useState("");
  const [orbActionError, setOrbActionError] = useState("");
  const [orbLoading, setOrbLoading] = useState(false);
  const [activeOrbTokenId, setActiveOrbTokenId] = useState("");
  const [accountError, setAccountError] = useState("");
  const [verificationSending, setVerificationSending] = useState(false);
  const [verificationRefreshing, setVerificationRefreshing] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [claimPhysicalCardOpen, setClaimPhysicalCardOpen] = useState(false);
  const [claimedPhysicalCards, setClaimedPhysicalCards] = useState([]);
  const [claimedPhysicalCardsLoading, setClaimedPhysicalCardsLoading] =
    useState(false);
  const [claimedPhysicalCardsError, setClaimedPhysicalCardsError] =
    useState("");
  const [inspectedPhysicalCardIndex, setInspectedPhysicalCardIndex] =
    useState(null);

  const { login: loginWithAGW, logout: logoutAGW } = useLoginWithAbstract();
  const {
    address: connectedAddress,
    chainId,
    isConnected,
    connector,
  } = useAccount();
  const { disconnect } = useDisconnect();
  const { address: signerAddress, status: signerStatus } =
    useGlobalWalletSignerAccount();


  const handleVerifyLegacy = async () => {
    try {
      const res = await verifyInfinityOrbsClient({
        wagmiConfig,
        agwAddress: userData?.abstractWalletAddress,
        userId: userData?.id,
      });

      setOrbImages(res.orbImages);
      setOrbVerifyMsg(
        `Verified ${res.orbCount} Infinity Orb${res.orbCount === 1 ? "" : "s"}.`,
      );
    } catch {
      setOrbVerifyMsg("Unable to verify your Infinity Orbs right now.");
    }
  };

  void handleVerifyLegacy;

  const loadOwnedOrbs = async () => {
    if (!userData?.id) {
      setOrbItems([]);
      return;
    }

    try {
      const snapshot = await getDocs(
        collection(db, "users", userData.id, "InfinityOrbs"),
      );
      const items = snapshot.docs
        .map((orbDoc) =>
          normalizeOrbRecord({ tokenId: orbDoc.id, ...orbDoc.data() }),
        )
        .filter((item) => item?.tokenId)
        .sort((a, b) => Number(a.tokenId || 0) - Number(b.tokenId || 0));
      setOrbItems(items);
    } catch {
      setOrbItems([]);
    }
  };

  const handleVerify = async () => {
    if (!userData?.abstractWalletAddress || !userData?.id) {
      setOrbActionError(
        "Connect and save your Abstract wallet before verifying your Infinity Orbs.",
      );
      return;
    }

    try {
      setOrbLoading(true);
      setOrbActionError("");
      setOrbActionMsg("");
      const res = await verifyInfinityOrbsClient({
        wagmiConfig,
        agwAddress: userData?.abstractWalletAddress,
        userId: userData?.id,
      });

      setOrbItems((res.orbAssets || []).map((orb) => normalizeOrbRecord(orb)));
      setOrbVerifyMsg(
        `Verified ${res.orbCount} Infinity Orb${res.orbCount === 1 ? "" : "s"}.`,
      );
    } catch (error) {
      setOrbActionError(
        error?.message || "Unable to verify your Infinity Orbs right now.",
      );
    } finally {
      setOrbLoading(false);
    }
  };

  const handleUseOrbAvatar = async (orb) => {
    if (!userData?.id || !orb?.tokenId) return;

    try {
      setOrbLoading(true);
      setActiveOrbTokenId(String(orb.tokenId));
      setOrbActionError("");
      setOrbActionMsg("");

      const composedImage = await composeOrbAvatarFromLayers(orb.layers, {
        fallbackImageUrl: orb.imageUrl,
      });
      let composedImageUrl = orb.imageUrl || "";

      if (composedImage && composedImage.startsWith("data:image/")) {
        try {
          const orbAvatarRef = storage.ref(
            `users/${userData.id}/orb_avatars/${orb.tokenId}.png`,
          );
          await orbAvatarRef.putString(composedImage, "data_url");
          composedImageUrl = await orbAvatarRef.getDownloadURL();
        } catch (uploadError) {
          console.warn(
            "Failed to upload composed orb avatar image, using fallback NFT image instead:",
            uploadError,
          );
          composedImageUrl = orb.imageUrl || "";
        }
      } else if (composedImage) {
        composedImageUrl = composedImage;
      }

      const orbAvatar = {
        tokenId: String(orb.tokenId),
        name: describeOrbSelection(orb),
        layers: orb.layers || [],
        composedImageUrl,
        imageUrl: orb.imageUrl || "",
        updatedAt: new Date(),
      };

      await updateDoc(doc(db, "users", userData.id), {
        orbAvatar,
        updatedAt: new Date(),
      });

      setOrbActionMsg(`${describeOrbSelection(orb)} is now your active avatar.`);
    } catch (error) {
      setOrbActionError(
        error?.message || "Unable to apply this orb as your avatar.",
      );
    } finally {
      setOrbLoading(false);
      setActiveOrbTokenId("");
    }
  };

  const handleClearOrbAvatar = async () => {
    if (!userData?.id) return;

    try {
      setOrbLoading(true);
      setOrbActionError("");
      setOrbActionMsg("");
      await updateDoc(doc(db, "users", userData.id), {
        orbAvatar: null,
        updatedAt: new Date(),
      });
      setOrbActionMsg("Reverted to your standard avatar.");
    } catch (error) {
      setOrbActionError(
        error?.message || "Unable to switch back to your standard avatar.",
      );
    } finally {
      setOrbLoading(false);
    }
  };

  useEffect(() => {
    if (userData?.abstractWalletAddress) {
      setRegisteredWallet(userData.abstractWalletAddress);
    } else {
      setRegisteredWallet("");
    }
  }, [userData?.abstractWalletAddress]);

  // Prefill from userData when modal opens / user changes
  useEffect(() => {
    if (!userData) return;

    const shippingAddress = userData.shippingAddress || {};

    setForm({
      displayName: userData.name ?? "",
      username: userData.username ?? "",
      email: userData.email ?? "",
      phone: userData.phone ?? "",
      xHandle: userData.xHandle ?? "",
      abstractWalletAddress:
        userData.abstractWalletAddress ?? "",
      country: shippingAddress.countryCode ?? userData.country ?? "",
      shippingFullName: shippingAddress.fullName ?? "",
      shippingLine1: shippingAddress.line1 ?? "",
      shippingLine2: shippingAddress.line2 ?? "",
      shippingCity: shippingAddress.city ?? "",
      shippingStateProvince: shippingAddress.stateProvince ?? "",
      shippingPostalCode: shippingAddress.postalCode ?? "",
    });
    setAccountError("");

    // Notifications default → on, unless user has explicit prefs
    // If they already have an X handle, start in "view" mode; otherwise edit mode
    setIsEditingX(!userData?.xHandle);
  }, [userData, visible]);

  useEffect(() => {
    if (!visible || !userData?.id) {
      setNotifications({ ...DEFAULT_NOTIFICATION_PREFERENCES });
      setNotificationsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setNotificationsLoading(true);
    setNotifications({ ...DEFAULT_NOTIFICATION_PREFERENCES });

    db.collection("users")
      .doc(userData.id)
      .collection("notifications")
      .doc(NOTIFICATION_DOC_ID)
      .get()
      .then((notificationSnap) => {
        if (cancelled) return;

        const nextPreferences = notificationSnap.exists
          ? normalizeNotificationPreferences(notificationSnap.data()?.preferences)
          : { ...DEFAULT_NOTIFICATION_PREFERENCES };
        setNotifications(nextPreferences);
      })
      .catch(() => {
        if (cancelled) return;
        setNotifications({ ...DEFAULT_NOTIFICATION_PREFERENCES });
      })
      .finally(() => {
        if (!cancelled) {
          setNotificationsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [visible, userData?.id]);

  useEffect(() => {
    if (!visible || !userData?.id) return;
    loadOwnedOrbs();
  }, [visible, userData?.id, userData?.orbCount, userData?.orbAvatar?.tokenId]);

  useEffect(() => {
    if (!visible || !userData?.id) {
      setClaimedPhysicalCards([]);
      setClaimedPhysicalCardsLoading(false);
      setClaimedPhysicalCardsError("");
      setInspectedPhysicalCardIndex(null);
      return undefined;
    }

    let cancelled = false;
    const claimsRef = db
      .collection("users")
      .doc(userData.id)
      .collection("claimedAssets");

    setClaimedPhysicalCardsLoading(true);
    setClaimedPhysicalCardsError("");

    const unsubscribe = claimsRef.onSnapshot(
      async (snapshot) => {
        try {
          const claims = snapshot.docs
            .map((claimDoc) => ({
              id: claimDoc.id,
              assetId: claimDoc.id,
              ...claimDoc.data(),
            }))
            .sort(
              (a, b) =>
                toMillis(b.claimedAt || b.createdAt) -
                toMillis(a.claimedAt || a.createdAt),
            );

          const hydrated = await Promise.all(
            claims.map(async (claim) => {
              const cardId = claim.cardId || claim.characterCardId;
              let cardData = null;

              if (cardId) {
                const userCardSnap = await db
                  .collection("users")
                  .doc(userData.id)
                  .collection("Character cards")
                  .doc(cardId)
                  .get();

                if (userCardSnap.exists) {
                  cardData = { id: userCardSnap.id, ...userCardSnap.data() };
                } else {
                  const globalCardSnap = await db
                    .collection("Character cards")
                    .doc(cardId)
                    .get();

                  if (globalCardSnap.exists) {
                    cardData = {
                      id: globalCardSnap.id,
                      ...globalCardSnap.data(),
                    };
                  }
                }
              }

              return {
                ...cardData,
                id: cardData?.id || cardId || claim.assetId,
                name: cardData?.name || "Physical Card",
                type: "Character",
                physicalAssetId: claim.assetId,
                claimedAt: claim.claimedAt || claim.createdAt || null,
                claimSource: claim.sourceRegistry || claim.source || "",
                amountOwned: cardData?.amountOwned || 1,
              };
            }),
          );

          if (!cancelled) {
            setClaimedPhysicalCards(hydrated);
            setClaimedPhysicalCardsLoading(false);
            setClaimedPhysicalCardsError("");
          }
        } catch {
          if (!cancelled) {
            setClaimedPhysicalCards([]);
            setClaimedPhysicalCardsLoading(false);
            setClaimedPhysicalCardsError(
              "Unable to load claimed physical cards right now.",
            );
          }
        }
      },
      () => {
        if (!cancelled) {
          setClaimedPhysicalCards([]);
          setClaimedPhysicalCardsLoading(false);
          setClaimedPhysicalCardsError(
            "Unable to load claimed physical cards right now.",
          );
        }
      },
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [visible, userData?.id]);

  // Reset when modal closes or when switching between mobile/desktop
  useEffect(() => {
    if (!visible) {
      setShowWalletOptions(false);
      setWalletConnecting(false);
      setActiveSection("account");
      setIsMobileOnList(true);
      setDefaultWalletDialogOpen(false);
      setPendingDefaultWallet("");
      setAccountError("");
      setVerificationMessage("");
      setVerificationError("");
      setClaimPhysicalCardOpen(false);
      setInspectedPhysicalCardIndex(null);
      setOrbVerifyMsg("");
      setOrbActionMsg("");
      setOrbActionError("");
      setOrbLoading(false);
      setActiveOrbTokenId("");
    } else if (!isMobile) {
      // desktop always shows detail view
      setIsMobileOnList(false);
    } else {
      // mobile starts on list view
      setIsMobileOnList(true);
    }
  }, [visible, isMobile]);

  useEffect(() => {
    if (!visible || !userData?.id || userData?.emailVerified) return;
    syncEmailVerificationStatus({
      silent: true,
      showPendingMessage: false,
    });
  }, [visible, userData?.id, userData?.emailVerified]);

  const handleChange = (field) => (e) => {
    setAccountError("");
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleClose = () => {
    if (saving || walletConnecting) return;
    onClose?.();
  };

  const handleOpenPolicy = (tab) => {
    onClose?.();
    navigate(`/policy?tab=${tab}`);
  };

  const syncEmailVerificationStatus = async ({
    silent = false,
    showPendingMessage = true,
  } = {}) => {
    if (!userData?.id) return false;

    const currentUser = auth.currentUser;
    if (!currentUser || currentUser.uid !== userData.id) {
      if (!silent) {
        setVerificationError(
          "Sign back in to refresh your email verification status.",
        );
      }
      return false;
    }

    try {
      setVerificationRefreshing(true);
      if (!silent) {
        setVerificationError("");
        setVerificationMessage("");
      }

      await currentUser.reload();
      const verified = !!auth.currentUser?.emailVerified;

      if (!verified) {
        if (!silent && showPendingMessage) {
          setVerificationMessage(
            "Your email is still unverified. After clicking the link in your inbox, come back here and refresh status.",
          );
        }
        return false;
      }

      if (!userData?.emailVerified) {
        await updateDoc(doc(db, "users", userData.id), {
          emailVerified: true,
          updatedAt: new Date(),
        });
      }

      if (!silent) {
        setVerificationMessage("Your email is verified.");
      }
      return true;
    } catch (err) {
      if (!silent) {
        setVerificationError(
          err?.message || "Unable to refresh email verification right now.",
        );
      }
      return false;
    } finally {
      setVerificationRefreshing(false);
    }
  };

  const handleSendVerificationEmail = async () => {
    if (!userData?.id) return;

    const currentUser = auth.currentUser;
    if (!currentUser || currentUser.uid !== userData.id) {
      setVerificationError(
        "Sign back in to send a verification email for this account.",
      );
      return;
    }

    try {
      setVerificationSending(true);
      setVerificationError("");
      setVerificationMessage("");
      await currentUser.sendEmailVerification();
      setVerificationMessage(
        "Verification email sent. Check your inbox and spam folder, then come back here and refresh status.",
      );
    } catch (err) {
      setVerificationError(
        err?.message || "Unable to send a verification email right now.",
      );
    } finally {
      setVerificationSending(false);
    }
  };

  const handleChangeWallet = async () => {
    // If a wallet is currently connected, prompt to disconnect first
    if (isConnected && disconnect) {
      const confirmed = window.confirm(
        "You currently have a wallet connected. To update your registered wallet, you must disconnect it first. Disconnect now?"
      );
      if (!confirmed) return;

      await disconnect();
    }

    // Now we are in "update" mode and can show AGW connect options
    setIsUpdateFlow(true);
    setShowWalletOptions((prev) => !prev || !registeredWallet);
  };

  const pickProviderForLabel = (eth, label) => {
    if (!eth) return null;

    const providers =
      eth.providers ||
      (Array.isArray(eth._providers) ? eth._providers : []) ||
      [];

    if (!providers.length) {
      return eth;
    }

    if (label === "MetaMask") {
      const mm =
        providers.find((p) => p.isMetaMask) ||
        providers.find((p) => /metamask/i.test(p?.name || ""));
      if (mm) return mm;
    }

    if (label === "AGW") {
      const agw =
        providers.find((p) => p.isAbstract) ||
        providers.find((p) => /abstract/i.test(p?.name || ""));
      if (agw) return agw;
    }

    return providers[0] || eth;
  };

  // ---------- DEFAULT WALLET HELPERS ----------

  const openDefaultWalletDialog = (address) => {
    if (!address) return;
    setPendingDefaultWallet(address);
    setDefaultWalletDialogOpen(true);
  };

  const ensureUserRef = () => {
    if (!userData?.id) {
      return null;
    }
    return doc(db, "users", userData.id);
  };

  const handleConfirmDefaultWallet = async () => {
    const userRef = ensureUserRef();
    if (!userRef || !pendingDefaultWallet) {
      setDefaultWalletDialogOpen(false);
      setPendingDefaultWallet("");
      return;
    }

    try {
      setSaving(true);

      // Future-friendly: store both legacy field and a "defaultWallets" object
      const updateData = {
        abstractWalletAddress: pendingDefaultWallet,
        defaultWallets: {
          ...(userData?.defaultWallets || {}),
          abstract: pendingDefaultWallet,
        },
        updatedAt: new Date(),
      };

      await updateDoc(userRef, updateData);

      setRegisteredWallet(pendingDefaultWallet);
      setForm((prev) => ({
        ...prev,
        abstractWalletAddress: pendingDefaultWallet,
      }));
      setIsUpdateFlow(false);
    } catch {
      alert("Unable to set default wallet. Please try again.");
    } finally {
      setSaving(false);
      setDefaultWalletDialogOpen(false);
      setPendingDefaultWallet("");
    }
  };

  const handleDismissDefaultWallet = () => {
    if (saving) return;
    setDefaultWalletDialogOpen(false);
    setPendingDefaultWallet("");
  };

  const handleNewAbstractConnection = (address) => {
    if (!address) return;

    setForm((prev) => ({
      ...prev,
      abstractWalletAddress: address,
    }));

    // If no default yet or different from current default, prompt user
    if (!registeredWallet || registeredWallet.toLowerCase() !== address.toLowerCase()) {
      openDefaultWalletDialog(address);
    }

    setIsUpdateFlow(true);
  };

  // ---------- WALLET CONNECTION ----------

  const connectWalletOnAbstract = async ({ label }) => {
    try {
      setWalletConnecting(true);
      const eth = typeof window !== "undefined" ? window.ethereum : undefined;

      if (!eth) {
        alert("No compatible wallet provider was found in your browser.");
        return;
      }

      const provider = pickProviderForLabel(eth, label);
      if (!provider) {
        alert("No compatible wallet provider was found for this label.");
        return;
      }

      const accounts = await provider.request({
        method: "eth_requestAccounts",
      });
      const address = accounts?.[0];

      if (!address) {
        throw new Error("No address returned from wallet.");
      }

      // Ensure chain is Abstract Testnet
      const targetChainId = `0x${abstractTestnet.id.toString(16)}`;
      const currentChainId = await provider.request({ method: "eth_chainId" });

      if (currentChainId !== targetChainId) {
        try {
          await provider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: targetChainId }],
          });
        } catch {
          alert(
            "Please switch your wallet network to Abstract Testnet and try again."
          );
          return;
        }
      }

      // New shared handler: updates form + prompts for default if needed
      handleNewAbstractConnection(address);

      setShowWalletOptions(false);
    } catch {
      alert("Failed to connect wallet. Check your wallet and try again.");
    } finally {
      setWalletConnecting(false);
    }
  };

  const handleConnectMetaMask = () =>
    connectWalletOnAbstract({ label: "MetaMask" });

  const handleConnectAGW = async () => {
    await loginWithAGW();

    // Try to use the freshest address we have (connectedAddress or signerAddress)
    const addr = connectedAddress || signerAddress;
    if (addr) {
      handleNewAbstractConnection(addr);
    }
  };

  // ---------- NOTIFICATIONS & SECTIONS ----------

  const handleNotificationToggle = (key) => (e) => {
    const checked = e.target.checked;
    setNotifications((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    if (isMobile) {
      setIsMobileOnList(false); // go to detail view on mobile
    }
  };

  const handleClearXHandle = () => {
    setForm((prev) => ({ ...prev, xHandle: "" }));
    setIsEditingX(true);
  };

  const handleSaveAccount = async () => {
    const userRef = ensureUserRef();
    if (!userRef) {
      onClose?.();
      return;
    }

    const validationError = getShippingAddressValidationError(form);
    if (validationError) {
      setAccountError(validationError);
      return;
    }

    try {
      setSaving(true);
      setAccountError("");
      const countryCode = trimValue(form.country).toUpperCase();
      const shippingAddress = hasShippingAddressDetails(form)
        ? {
            fullName: trimValue(form.shippingFullName),
            line1: trimValue(form.shippingLine1),
            line2: trimValue(form.shippingLine2),
            city: trimValue(form.shippingCity),
            stateProvince: trimValue(form.shippingStateProvince),
            postalCode: trimValue(form.shippingPostalCode),
            countryCode,
          }
        : null;
      const updateData = {
        username: trimValue(form.username) || null,
        phone: trimValue(form.phone) || null,
        country: countryCode || null,
        shippingAddress,
        updatedAt: new Date(),
      };
      await updateDoc(userRef, updateData);
    } catch {
      alert("Unable to save account changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveIntegrations = async () => {
    const userRef = ensureUserRef();
    if (!userRef) {
      onClose?.();
      return;
    }

    try {
      setSaving(true);

      const updateData = {
        xHandle: form.xHandle || null,
        updatedAt: new Date(),
      };

      // Keep existing behavior: if user explicitly updated wallet via this flow
      if (isUpdateFlow && form.abstractWalletAddress) {
        updateData.abstractWalletAddress = form.abstractWalletAddress;
        updateData.defaultWallets = {
          ...(userData?.defaultWallets || {}),
          abstract: form.abstractWalletAddress,
        };
      }

      await updateDoc(userRef, updateData);

      if (updateData.abstractWalletAddress) {
        setRegisteredWallet(updateData.abstractWalletAddress);
      }

      setIsUpdateFlow(false);
    } catch {
      alert("Unable to save integrations. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    const userRef = ensureUserRef();
    if (!userRef || !userData?.id) {
      onClose?.();
      return;
    }

    try {
      setSaving(true);
      const notificationRef = db
        .collection("users")
        .doc(userData.id)
        .collection("notifications")
        .doc(NOTIFICATION_DOC_ID);
      const nextPreferences = normalizeNotificationPreferences(notifications);
      const notificationSnap = await notificationRef.get();
      const notificationUpdate = {
        preferences: nextPreferences,
        updatedAt: new Date(),
      };

      if (!notificationSnap.exists) {
        notificationUpdate.createdAt = new Date();
      }

      await notificationRef.set(notificationUpdate, { merge: true });

      setNotifications(nextPreferences);
    } catch {
      alert("Unable to save notification settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const sharedTextFieldSx = {
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      "& fieldset": { borderColor: "#4a3ab7" },
      "&:hover fieldset": { borderColor: "#6a58ff" },
      "&.Mui-focused fieldset": { borderColor: "#ffd24d" },
    },
    "& .MuiFormHelperText-root": { color: "#9aa0ff" },
  };

  const sharedSelectSx = {
    color: "#fff",
    "& .MuiSelect-select": {
      color: "#fff",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#4a3ab7",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6a58ff",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#ffd24d",
    },
    "& .MuiSvgIcon-root": { color: "#c9b7ff" },
  };

  const renderAccountSection = () => (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Typography
        variant="body2"
        sx={{ opacity: 0.85, color: "#e2d5ff", fontWeight: "bold" }}
      >
        Your Account
      </Typography>

      <Box>
        <Typography
          variant="caption"
          sx={{ color: "#9aa0ff", textTransform: "uppercase" }}
        >
          Name
        </Typography>
        <Typography sx={{ color: "#fff", mt: 0.5 }}>
          {form.displayName || userData?.name || "—"}
        </Typography>
      </Box>

      <TextField
        label="Username"
        variant="outlined"
        fullWidth
        value={form.username}
        onChange={handleChange("username")}
        helperText="This is your public handle in Royale."
        InputLabelProps={{ sx: { color: "#c9b7ff" } }}
        sx={sharedTextFieldSx}
      />

      <Box>
        <Typography
          variant="caption"
          sx={{ color: "#9aa0ff", textTransform: "uppercase" }}
        >
          Email
        </Typography>
        <Typography sx={{ color: "#fff", mt: 0.5 }}>
          {form.email || userData?.email || "—"}
        </Typography>
      </Box>

      <Box
        sx={{
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 2,
          p: 2,
          background: "rgba(255,255,255,0.02)",
        }}
      >
        <Stack spacing={1.5}>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ color: "#e2d5ff", fontWeight: 700, mb: 0.5 }}
            >
              Email Verification
            </Typography>
            <Typography variant="body2" sx={{ color: "#9aa0ff" }}>
              Email verification is optional overall, but it is required for
              Party and Lobby chat and for ambassador referral ETH bonus
              eligibility.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              alignSelf: "flex-start",
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              bgcolor: userData?.emailVerified
                ? "rgba(61,214,140,0.16)"
                : "rgba(255,210,77,0.14)",
              color: userData?.emailVerified ? "#8ff0bf" : "#ffd973",
              border: userData?.emailVerified
                ? "1px solid rgba(61,214,140,0.28)"
                : "1px solid rgba(255,210,77,0.24)",
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: 0.2,
            }}
          >
            {userData?.emailVerified ? "Verified" : "Not Verified"}
          </Box>

          {!userData?.emailVerified ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <Button
                variant="contained"
                onClick={handleSendVerificationEmail}
                disabled={
                  verificationSending || verificationRefreshing || !form.email
                }
                sx={{
                  fontWeight: 700,
                  bgcolor: "#6f52ed",
                  "&:hover": { bgcolor: "#5a3ed3" },
                }}
              >
                {verificationSending
                  ? "Sending..."
                  : "Send Verification Email"}
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  syncEmailVerificationStatus({ silent: false })
                }
                disabled={verificationRefreshing}
                sx={{
                  color: "#fff",
                  borderColor: "#6a58ff",
                  "&:hover": {
                    borderColor: "#ffd24d",
                    bgcolor: "rgba(255,255,255,0.03)",
                  },
                }}
              >
                {verificationRefreshing ? "Refreshing..." : "Refresh Status"}
              </Button>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: "#8ff0bf" }}>
              Your verified email unlocks chat and keeps ambassador referral
              rewards eligible.
            </Typography>
          )}

          {verificationMessage ? (
            <Typography variant="body2" sx={{ color: "#cfd6ff" }}>
              {verificationMessage}
            </Typography>
          ) : null}

          {verificationError ? (
            <Typography variant="body2" sx={{ color: "#ffb3c7" }}>
              {verificationError}
            </Typography>
          ) : null}
        </Stack>
      </Box>

      <TextField
        label="Phone Number"
        variant="outlined"
        fullWidth
        type="tel"
        value={form.phone}
        onChange={handleChange("phone")}
        helperText="Optional, used for important account notices."
        InputLabelProps={{ sx: { color: "#c9b7ff" } }}
        sx={sharedTextFieldSx}
      />

      <Box
        sx={{
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 2,
          p: 2,
          background: "rgba(255,255,255,0.02)",
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ color: "#e2d5ff", fontWeight: 700, mb: 0.5 }}
            >
              Shipping Address
            </Typography>
            <Typography variant="body2" sx={{ color: "#9aa0ff" }}>
              Add a mailing address for physical pack shipping. Country can also
              be saved on its own for analytics.
            </Typography>
          </Box>

          <FormControl fullWidth>
            <InputLabel id="account-country-label" sx={{ color: "#c9b7ff" }}>
              Country
            </InputLabel>
            <Select
              labelId="account-country-label"
              label="Country"
              value={form.country}
              onChange={handleChange("country")}
              sx={sharedSelectSx}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 360,
                  },
                },
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {countryCodes.map(({ country, code, flag }) => (
                <MenuItem key={code} value={code}>
                  {flag} {country}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Full Name"
            variant="outlined"
            fullWidth
            value={form.shippingFullName}
            onChange={handleChange("shippingFullName")}
            helperText="Who should receive the shipment."
            InputLabelProps={{ sx: { color: "#c9b7ff" } }}
            sx={sharedTextFieldSx}
          />

          <TextField
            label="Address Line 1"
            variant="outlined"
            fullWidth
            value={form.shippingLine1}
            onChange={handleChange("shippingLine1")}
            helperText="Street address, P.O. box, company name, c/o."
            InputLabelProps={{ sx: { color: "#c9b7ff" } }}
            sx={sharedTextFieldSx}
          />

          <TextField
            label="Address Line 2"
            variant="outlined"
            fullWidth
            value={form.shippingLine2}
            onChange={handleChange("shippingLine2")}
            helperText="Apartment, suite, unit, building, floor, etc. (optional)"
            InputLabelProps={{ sx: { color: "#c9b7ff" } }}
            sx={sharedTextFieldSx}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label="City"
              variant="outlined"
              fullWidth
              value={form.shippingCity}
              onChange={handleChange("shippingCity")}
              InputLabelProps={{ sx: { color: "#c9b7ff" } }}
              sx={sharedTextFieldSx}
            />

            <TextField
              label="State / Province / Region"
              variant="outlined"
              fullWidth
              value={form.shippingStateProvince}
              onChange={handleChange("shippingStateProvince")}
              helperText="Optional"
              InputLabelProps={{ sx: { color: "#c9b7ff" } }}
              sx={sharedTextFieldSx}
            />
          </Box>

          <TextField
            label="Postal / ZIP Code"
            variant="outlined"
            fullWidth
            value={form.shippingPostalCode}
            onChange={handleChange("shippingPostalCode")}
            InputLabelProps={{ sx: { color: "#c9b7ff" } }}
            sx={sharedTextFieldSx}
          />
        </Stack>
      </Box>

      {accountError ? (
        <Typography variant="body2" sx={{ color: "#ffb3c7" }}>
          {accountError}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          pt: 1,
        }}
      >
        <Button
          variant="contained"
          onClick={handleSaveAccount}
          disabled={saving}
          sx={{
            fontWeight: 700,
            bgcolor: "#6f52ed",
            "&:hover": { bgcolor: "#5a3ed3" },
          }}
        >
          {saving ? "Saving…" : "Save Account"}
        </Button>
      </Box>
    </Stack>
  );

  const renderIntegrationsSection = () => {
    const truncate = (addr) =>
      addr ? `${addr.slice(0, 6)}…${addr.slice(-6)}` : "";

    const hasDefaultAbstract = !!registeredWallet;

    const connectedIsDefault =
      isConnected &&
      connectedAddress &&
      registeredWallet &&
      connectedAddress.toLowerCase() === registeredWallet.toLowerCase();

    const connectedIsNonDefault =
      isConnected &&
      connectedAddress &&
      (!registeredWallet ||
        connectedAddress.toLowerCase() !== registeredWallet.toLowerCase());

    return (
      <Stack spacing={3} sx={{ mt: 1 }}>
        <Typography
          variant="body2"
          sx={{ opacity: 0.85, color: "#e2d5ff", fontWeight: "bold" }}
        >
          Integrations
        </Typography>

        {/* Social Accounts */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ color: "#e2d5ff", mb: 0.5, fontWeight: 600 }}
          >
            Social Accounts
          </Typography>
          <Typography variant="body2" sx={{ color: "#9aa0ff", mb: 1 }}>
            Link your X handle so we know who to tag.
          </Typography>

          {isEditingX || !form.xHandle ? (
            <TextField
              label="X / Twitter Handle"
              variant="outlined"
              fullWidth
              value={form.xHandle}
              onChange={handleChange("xHandle")}
              placeholder="@yourhandle"
              InputLabelProps={{ sx: { color: "#c9b7ff" } }}
              sx={sharedTextFieldSx}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1,
                borderRadius: 2,
                border: "1px solid #4a3ab7",
                bgcolor: "rgba(74,58,183,0.05)",
              }}
            >
              <Typography sx={{ color: "#fff", flexGrow: 1 }}>
                X: {form.xHandle}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setIsEditingX(true)}
                sx={{ color: "#c9b7ff" }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleClearXHandle}
                sx={{ color: "#ff8a80" }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        <Divider
          sx={{
            borderColor: "rgba(255,255,255,0.1)",
          }}
        />

        {/* Wallets group – future-friendly for Base/Solana/etc. */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ color: "#e2d5ff", mb: 1, fontWeight: 600 }}
          >
            Wallets
          </Typography>

          {/* Abstract Wallet */}
          <Box
            sx={{
              borderRadius: 2,
              border: "1px solid rgba(255,255,255,0.12)",
              p: 1.5,
              mb: 1.5,
              bgcolor: "rgba(0,0,0,0.3)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <img
                src={require("../@shared/assets/icons/abstract.jpeg")}
                alt=""
                style={{ height: 40, borderRadius: 8, marginRight: 8 }}
              />
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "#e2d5ff", fontWeight: 600 }}
                >
                  Abstract Wallet
                </Typography>
                <Typography variant="caption" sx={{ color: "#9aa0ff" }}>
                  Abstract (L2) — wagers, rewards & payouts.
                </Typography>
              </Box>
            </Box>

            {/* Default Abstract Wallet status */}
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                sx={{ color: "#9aa0ff", textTransform: "uppercase" }}
              >
                Default Abstract Wallet
              </Typography>
              {hasDefaultAbstract ? (
                <Typography
                  variant="body2"
                  sx={{ color: "#e2d5ff", mt: 0.25, fontWeight: 500 }}
                >
                  {truncate(registeredWallet)}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: "#ffb74d", mt: 0.25, fontWeight: 500 }}
                >
                  No default Abstract wallet set.
                </Typography>
              )}
            </Box>

            {/* Connection status */}
            {isConnected && connectedAddress ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ color: "#e2d5ff" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: "#0f0",
                      marginRight: 8,
                    }}
                  />
                  Connected: {truncate(connectedAddress)}
                </Typography>

                {connectedIsDefault && (
                  <Typography
                    variant="caption"
                    sx={{ color: "#9aa0ff", display: "block", mt: 0.5 }}
                  >
                    This wallet is currently your default Abstract wallet.
                  </Typography>
                )}

                {connectedIsNonDefault && (
                  <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#ffb74d", alignSelf: "center" }}
                    >
                      This connected wallet is not your default.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openDefaultWalletDialog(connectedAddress)}
                      disabled={saving || walletConnecting}
                      sx={{
                        borderColor: "#ffd24d",
                        color: "#ffd24d",
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": {
                          borderColor: "#ffe082",
                          backgroundColor: "rgba(255,210,77,0.12)",
                        },
                      }}
                    >
                      Set as default
                    </Button>
                  </Box>
                )}

                <Button
                  variant="text"
                  sx={{ color: "#6f52ed", mt: 1 }}
                  onClick={async () => {
                    await disconnect();
                  }}
                >
                  Disconnect?
                </Button>
              </Box>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ color: "#e2d5ff" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: "#888",
                      marginRight: 8,
                    }}
                  />
                  No wallet currently connected —{" "}
                  <Button
                    variant="text"
                    sx={{
                      color: "#6f52ed",
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                    onClick={handleConnectAGW}
                  >
                    Connect now
                  </Button>
                </Typography>
              </Box>
            )}

            {/* AGW connect button (when not connected and explicitly toggled) */}
            {showWalletOptions && !isConnected && (
              <Stack direction="column" spacing={1} sx={{ mt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleConnectAGW}
                  disabled={walletConnecting}
                  sx={{
                    borderColor: "#22c1c3",
                    color: "#e2d5ff",
                    width: "100%",
                    "&:hover": {
                      borderColor: "#3de0e2",
                      backgroundColor: "rgba(34,193,195,0.12)",
                    },
                  }}
                >
                  {walletConnecting
                    ? "Connecting…"
                    : "Connect AGW (Abstract Wallet)"}
                </Button>
              </Stack>
            )}
          </Box>

          {process.env.REACT_APP_ENABLE_NFT_WALLET_SCANNING !== "false" ? (
            <>
              <WalletScanCard providerKey="phantom" />
              <WalletScanCard providerKey="metamask" />
            </>
          ) : null}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            pt: 1,
          }}
        >
          <Button
            variant="contained"
            onClick={handleSaveIntegrations}
            disabled={saving || walletConnecting}
            sx={{
              fontWeight: 700,
              bgcolor: "#6f52ed",
              "&:hover": { bgcolor: "#5a3ed3" },
            }}
          >
            {saving ? "Saving…" : "Save Integrations"}
          </Button>
        </Box>
      </Stack>
    );
  };

  const renderOrbsSectionLegacy = () => {
    const truncate = (addr) =>
      addr ? `${addr.slice(0, 6)}…${addr.slice(-6)}` : "";

    const hasDefaultAbstract = !!registeredWallet;

    const connectedIsDefault =
      isConnected &&
      connectedAddress &&
      registeredWallet &&
      connectedAddress.toLowerCase() === registeredWallet.toLowerCase();

    const connectedIsNonDefault =
      isConnected &&
      connectedAddress &&
      (!registeredWallet ||
        connectedAddress.toLowerCase() !== registeredWallet.toLowerCase());

    return (
      <Stack spacing={3} sx={{ mt: 1 }}>
        <Typography
          variant="body2"
          sx={{ opacity: 0.85, color: "#e2d5ff", fontWeight: "bold" }}
        >
          Infinity Orbs
        </Typography>

        {/* Social Accounts */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ color: "#e2d5ff", mb: 0.5, fontWeight: 600 }}
          >
            Social Accounts
          </Typography>
          <Typography variant="body2" sx={{ color: "#9aa0ff", mb: 1 }}>
            {!userData?.orbCount ? "You haven't verified that you own any Infinity Orbs" : "See your Infinity Orbs below"}
          </Typography>

        </Box>

        <Divider
          sx={{
            borderColor: "rgba(255,255,255,0.1)",
          }}
        />

        {/* Wallets group – future-friendly for Base/Solana/etc. */}
        <Box>
         

          {/* A Grid List of Infinty Orbs they Own */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 1,
            }}
          >
            {(orbImages || []).map((src, idx) => (
              <Box
                key={`${src}-${idx}`}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.12)",
                  bgcolor: "rgba(0,0,0,0.25)",
                  aspectRatio: "1 / 1",
                }}
              >
                <img
                  src={src}
                  alt={`Infinity Orb ${idx + 1}`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
            ))}
          </Box>

          {orbVerifyMsg || userData?.orbCount  && (
            <Typography variant="body2" sx={{ color: "#9aa0ff", mt: 1 }}>
              {orbVerifyMsg || `✅ Verified: ${userData.orbCount} orbs.`}
            </Typography>
          )}


        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            pt: 1,
          }}
        >
          <Button
            variant="contained"
            onClick={handleVerify}
            // disabled={saving || walletConnecting}
            sx={{
              fontWeight: 700,
              bgcolor: "#6f52ed",
              "&:hover": { bgcolor: "#5a3ed3" },
            }}
          >
            {saving ? "Saving…" : "Check & Verify"}
          </Button>
        </Box>
      </Stack>
    );
  };

  void renderOrbsSectionLegacy;

  const renderOrbsSection = () => {
    const selectedOrbTokenId = String(userData?.orbAvatar?.tokenId || "");
    const hasOrbSelection = !!selectedOrbTokenId;

    return (
      <Stack spacing={3} sx={{ mt: 1 }}>
        <Typography
          variant="body2"
          sx={{ opacity: 0.85, color: "#e2d5ff", fontWeight: "bold" }}
        >
          Infinity Orbs
        </Typography>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ color: "#e2d5ff", mb: 0.5, fontWeight: 600 }}
          >
            Verified Collection
          </Typography>
          <Typography variant="body2" sx={{ color: "#9aa0ff", mb: 1 }}>
            {!userData?.orbCount
              ? "Verify your wallet to load the Infinity Orbs you own."
              : "Select one verified orb to use as your active avatar."}
          </Typography>
        </Box>

        <Divider
          sx={{
            borderColor: "rgba(255,255,255,0.1)",
          }}
        />

        <Box>
          {orbActionError && (
            <Typography variant="body2" sx={{ color: "#ff9aa2", mb: 1 }}>
              {orbActionError}
            </Typography>
          )}
          {(orbVerifyMsg || orbActionMsg || userData?.orbCount) && (
            <Typography variant="body2" sx={{ color: "#9aa0ff", mb: 1 }}>
              {orbActionMsg ||
                orbVerifyMsg ||
                `Verified ${userData?.orbCount || 0} Infinity Orb${userData?.orbCount === 1 ? "" : "s"}.`}
            </Typography>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
              },
              gap: 1.5,
            }}
          >
            {(orbItems || []).map((orb) => {
              const isSelected = selectedOrbTokenId === String(orb.tokenId);
              const isApplying = activeOrbTokenId === String(orb.tokenId);
              const layerSourceMeta = getOrbLayerSourceMeta(orb);
              const canUseAsAvatar = !!layerSourceMeta.isAvailable;

              return (
                <Box
                  key={orb.tokenId}
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    border: isSelected
                      ? "1px solid #ffd24d"
                      : "1px solid rgba(255,255,255,0.12)",
                    bgcolor: "rgba(0,0,0,0.25)",
                    p: 1,
                  }}
                >
                  <img
                    src={orb.imageUrl}
                    alt={describeOrbSelection(orb)}
                    style={{
                      width: "100%",
                      aspectRatio: "1 / 1",
                      objectFit: "cover",
                      borderRadius: 10,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: "#e2d5ff", mt: 1, fontWeight: 700 }}
                  >
                    {describeOrbSelection(orb)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#9aa0ff", display: "block", mb: 1 }}
                  >
                    {layerSourceMeta.detail}
                  </Typography>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      mb: 1,
                      px: 1,
                      py: 0.35,
                      borderRadius: 999,
                      bgcolor:
                        layerSourceMeta.source === "storage"
                          ? "rgba(61, 214, 140, 0.18)"
                          : layerSourceMeta.source === "metadata"
                            ? "rgba(122, 162, 255, 0.18)"
                            : "rgba(255, 210, 77, 0.18)",
                      border:
                        layerSourceMeta.source === "storage"
                          ? "1px solid rgba(61, 214, 140, 0.45)"
                          : layerSourceMeta.source === "metadata"
                            ? "1px solid rgba(122, 162, 255, 0.45)"
                            : "1px solid rgba(255, 210, 77, 0.45)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          layerSourceMeta.source === "storage"
                            ? "#8ff0bf"
                            : layerSourceMeta.source === "metadata"
                              ? "#a9c3ff"
                              : "#ffd24d",
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {layerSourceMeta.label}
                    </Typography>
                  </Box>
                  <Button
                    fullWidth
                    size="small"
                    variant={isSelected ? "outlined" : "contained"}
                    onClick={() => handleUseOrbAvatar(orb)}
                    disabled={orbLoading || !canUseAsAvatar}
                    sx={{
                      fontWeight: 700,
                      textTransform: "none",
                      bgcolor: isSelected ? "transparent" : "#6f52ed",
                      borderColor: "#ffd24d",
                      color: isSelected ? "#ffd24d" : "#fff",
                      opacity: canUseAsAvatar ? 1 : 0.6,
                      "&:hover": {
                        bgcolor: isSelected
                          ? "rgba(255,210,77,0.10)"
                          : "#5a3ed3",
                      },
                    }}
                  >
                    {!canUseAsAvatar
                      ? "Unavailable"
                      : isApplying
                      ? "Applying..."
                      : isSelected
                        ? "Active Orb Avatar"
                        : "Use as Avatar"}
                  </Button>
                </Box>
              );
            })}
          </Box>

          {!orbItems.length && (
            <Typography variant="body2" sx={{ color: "#9aa0ff", mt: 1 }}>
              No verified orbs are loaded yet.
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            pt: 1,
          }}
        >
          {hasOrbSelection && (
            <Button
              variant="outlined"
              onClick={handleClearOrbAvatar}
              disabled={orbLoading}
              sx={{
                fontWeight: 700,
                borderColor: "rgba(255,255,255,0.25)",
                color: "#e2d5ff",
                textTransform: "none",
              }}
            >
              Use Standard Avatar
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleVerify}
            disabled={orbLoading}
            sx={{
              fontWeight: 700,
              bgcolor: "#6f52ed",
              "&:hover": { bgcolor: "#5a3ed3" },
            }}
          >
            {orbLoading ? "Working..." : "Check & Verify"}
          </Button>
        </Box>
      </Stack>
    );
  };

  const renderNotificationsSection = () => (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Typography
        variant="body2"
        sx={{ opacity: 0.85, color: "#e2d5ff", fontWeight: "bold" }}
      >
        Notifications
      </Typography>

      <Typography variant="body2" sx={{ color: "#9aa0ff" }}>
        Choose how you’d like to be notified about matches, rewards, and
        account activity.
      </Typography>

      {notificationsLoading ? (
        <Typography variant="body2" sx={{ color: "#cfc2ff" }}>
          Loading your current notification preferences…
        </Typography>
      ) : null}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1,
        }}
      >
        <Typography sx={{ color: "#e2d5ff" }}>SMS</Typography>
        <Switch
          checked={!!notifications.sms}
          onChange={handleNotificationToggle("sms")}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1,
        }}
      >
        <Typography sx={{ color: "#e2d5ff" }}>Email</Typography>
        <Switch
          checked={!!notifications.email}
          onChange={handleNotificationToggle("email")}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1,
        }}
      >
        <Typography sx={{ color: "#e2d5ff" }}>Push</Typography>
        <Switch
          checked={!!notifications.push}
          onChange={handleNotificationToggle("push")}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          pt: 1,
        }}
      >
        <Button
          variant="contained"
          onClick={handleSaveNotifications}
          disabled={saving || notificationsLoading}
          sx={{
            fontWeight: 700,
            bgcolor: "#6f52ed",
            "&:hover": { bgcolor: "#5a3ed3" },
          }}
        >
          {saving ? "Saving…" : "Save Notifications"}
        </Button>
      </Box>
    </Stack>
  );

  const renderPhysicalCardsSection = () => (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Typography
        variant="body2"
        sx={{ opacity: 0.85, color: "#e2d5ff", fontWeight: "bold" }}
      >
        Physical Cards
      </Typography>

      <Box
        sx={{
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 2,
          p: 2,
          background: "rgba(255,255,255,0.02)",
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ color: "#e2d5ff", fontWeight: 700, mb: 0.5 }}
            >
              Claim a Digital Character Card
            </Typography>
            <Typography variant="body2" sx={{ color: "#9aa0ff" }}>
              Redeem the asset ID printed on your physical card to add the
              matching digital card to your inventory.
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={() => setClaimPhysicalCardOpen(true)}
            sx={{
              alignSelf: "flex-start",
              fontWeight: 700,
              bgcolor: "#6f52ed",
              "&:hover": { bgcolor: "#5a3ed3" },
            }}
          >
            Claim Physical Card
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 2,
          p: 2,
          background: "rgba(255,255,255,0.02)",
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ color: "#e2d5ff", fontWeight: 700, mb: 0.5 }}
            >
              Claimed Physical Cards
            </Typography>
            <Typography variant="body2" sx={{ color: "#9aa0ff" }}>
              Physical cards you have already redeemed.
            </Typography>
          </Box>

          {claimedPhysicalCardsError ? (
            <Typography variant="body2" sx={{ color: "#ffb3c7" }}>
              {claimedPhysicalCardsError}
            </Typography>
          ) : null}

          {claimedPhysicalCardsLoading ? (
            <Typography variant="body2" sx={{ color: "#cfd6ff" }}>
              Loading claimed cards...
            </Typography>
          ) : null}

          {!claimedPhysicalCardsLoading &&
          !claimedPhysicalCardsError &&
          claimedPhysicalCards.length === 0 ? (
            <Box
              sx={{
                border: "1px dashed rgba(201,183,255,0.35)",
                borderRadius: 2,
                p: 2,
                bgcolor: "rgba(35,21,67,0.6)",
                textAlign: "center",
              }}
            >
              <Typography variant="body2" sx={{ color: "#c9b7ff" }}>
                No physical cards claimed yet.
              </Typography>
            </Box>
          ) : null}

          {claimedPhysicalCards.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(3, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                },
                gap: 1.5,
              }}
            >
              {claimedPhysicalCards.map((card, index) => (
                <Box
                  key={`${card.physicalAssetId}-${card.id}`}
                  onClick={() => setInspectedPhysicalCardIndex(index)}
                  sx={{
                    border: "1px dashed rgba(201,183,255,0.35)",
                    borderRadius: 2,
                    p: 1,
                    bgcolor: "#2a184f",
                    cursor: "pointer",
                    minWidth: 0,
                    "&:hover": {
                      borderColor: "#ffd24d",
                      bgcolor: "rgba(111,82,237,0.22)",
                    },
                  }}
                >
                  <Box sx={{ height: 166, mb: 1 }}>
                    {card.imageUrl ? (
                      <CharacterCard
                        {...card}
                        containerHeight={160}
                        heightPer={100}
                        shine={false}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: "100%",
                          borderRadius: 2,
                          bgcolor: "#231543",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          px: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "#c9b7ff", fontWeight: 700 }}
                        >
                          {card.name}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#e2d5ff",
                      fontWeight: 800,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {card.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#ffd24d",
                      display: "block",
                      fontWeight: 700,
                    }}
                  >
                    {card.physicalAssetId}
                  </Typography>
                  {formatClaimedAt(card.claimedAt) ? (
                    <Typography
                      variant="caption"
                      sx={{ color: "#9aa0ff", display: "block" }}
                    >
                      Claimed {formatClaimedAt(card.claimedAt)}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Box>
          ) : null}
        </Stack>
      </Box>

      <SwipeInspectModal
        open={inspectedPhysicalCardIndex !== null}
        onClose={() => setInspectedPhysicalCardIndex(null)}
        cards={claimedPhysicalCards}
        startIndex={inspectedPhysicalCardIndex ?? 0}
      />
    </Stack>
  );

  const sections = userData?.abstractWalletAddress ? [
    { id: "account", label: "Your Account" },
    { id: "notifications", label: "Notifications" },
    { id: "integrations", label: "Integrations" },
    { id: "physicalCards", label: "Physical Cards" },
    { id: "orbs", label: "Infinity Orbs" },
  ] : [
    { id: "account", label: "Your Account" },
    { id: "notifications", label: "Notifications" },
    { id: "integrations", label: "Integrations" },
    { id: "physicalCards", label: "Physical Cards" },
  ];

  // MOBILE: list of sections
  const renderMobileSectionList = () => (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ color: "#e2d5ff", mb: 1, fontWeight: 700 }}
      >
        Settings
      </Typography>
      <Stack spacing={1.2}>
        {sections.map((section) => (
          <Box
            key={section.id}
            onClick={() => handleSectionChange(section.id)}
            sx={{
              px: 1.5,
              py: 1.25,
              borderRadius: 2,
              cursor: "pointer",
              bgcolor: "rgba(111,82,237,0.08)",
              border: "1px solid rgba(111,82,237,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              "&:active": { transform: "scale(0.99)" },
            }}
          >
            <Typography sx={{ color: "#e2d5ff", fontWeight: 600 }}>
              {section.label}
            </Typography>
            <ArrowForwardIcon sx={{ color: "#e2d5ff" }} />
          </Box>
        ))}
      </Stack>

      <Box
        sx={{
          mt: 3,
          pt: 1.5,
          borderTop: "1px dashed rgba(255,255,255,0.12)",
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "#9aa0ff", mb: 1, textTransform: "uppercase" }}
        >
          More
        </Typography>
        <Typography
          component="button"
          type="button"
          variant="body2"
          onClick={() => handleOpenPolicy("terms")}
          sx={policyLinkSx}
        >
          Terms
        </Typography>
        <Typography
          component="button"
          type="button"
          variant="body2"
          onClick={() => handleOpenPolicy("privacy")}
          sx={policyLinkSx}
        >
          Privacy
        </Typography>
      </Box>
    </Box>
  );

  // MOBILE: detail view for current section
  const renderMobileSectionDetail = () => (
    <Box
      sx={{
        p: 2,
        maxHeight: "65vh",
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <IconButton
          size="small"
          onClick={() => setIsMobileOnList(true)}
          sx={{ mr: 1, color: "#e2d5ff" }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ color: "#e2d5ff", fontWeight: 600 }}>
          {sections.find((s) => s.id === activeSection)?.label ?? "Settings"}
        </Typography>
      </Box>

      {activeSection === "account" && renderAccountSection()}
      {activeSection === "physicalCards" && renderPhysicalCardsSection()}
      {activeSection === "integrations" && renderIntegrationsSection()}
      {activeSection === "orbs" && renderOrbsSection()}
      {activeSection === "notifications" && renderNotificationsSection()}
    </Box>
  );

  // DESKTOP: sidebar + content
  const renderDesktopLayout = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        minHeight: 360,
      }}
    >
      {/* LEFT: Sections list */}
      <Box
        sx={{
          borderRight: "1px solid rgba(255,255,255,0.08)",
          p: 2,
          width: 220,
          bgcolor: "rgba(0,0,0,0.25)",
        }}
      >
        <Stack spacing={1}>
          {sections.map((section) => (
            <Box
              key={section.id}
              onClick={() => handleSectionChange(section.id)}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2,
                cursor: "pointer",
                bgcolor:
                  activeSection === section.id
                    ? "rgba(111,82,237,0.15)"
                    : "transparent",
                border:
                  activeSection === section.id
                    ? "1px solid #6f52ed"
                    : "1px solid transparent",
                "&:hover": {
                  bgcolor: "rgba(111,82,237,0.10)",
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color:
                    activeSection === section.id ? "#ffd24d" : "#e2d5ff",
                  fontWeight: activeSection === section.id ? 700 : 500,
                }}
              >
                {section.label}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* Terms & Privacy */}
        <Box
          sx={{
            mt: 2,
            pt: 1,
            borderTop: "1px dashed rgba(255,255,255,0.1)",
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "#9aa0ff", mb: 1, textTransform: "uppercase" }}
          >
            More
          </Typography>
          <Typography
            component="button"
            type="button"
            variant="body2"
            onClick={() => handleOpenPolicy("terms")}
            sx={policyLinkSx}
          >
            Terms
          </Typography>
          <Typography
            component="button"
            type="button"
            variant="body2"
            onClick={() => handleOpenPolicy("privacy")}
            sx={policyLinkSx}
          >
            Privacy
          </Typography>
        </Box>
      </Box>

      {/* RIGHT: Section content */}
      <Box
        sx={{
          flex: 1,
          p: 2,
          maxHeight: "65vh",
          overflowY: "auto",
        }}
      >
        {activeSection === "account" && renderAccountSection()}
        {activeSection === "physicalCards" && renderPhysicalCardsSection()}
        {activeSection === "integrations" && renderIntegrationsSection()}
        {activeSection === "orbs" && renderOrbsSection()}
        {activeSection === "notifications" && renderNotificationsSection()}
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog
        open={!!visible}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        aria-labelledby="account-settings-title"
        PaperProps={{
          sx: {
            ...dialogPaperSx,
          },
        }}
      >
        <DialogTitle
          id="account-settings-title"
          sx={{
            ...dialogTitleSx,
            ...dialogTitleTextSx,
          }}
        >
          Account Settings
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            background:
              "radial-gradient(circle at top, #3b1f8a33 0%, transparent 55%)",
            p: 0,
          }}
        >
          {isMobile
            ? isMobileOnList
              ? renderMobileSectionList()
              : renderMobileSectionDetail()
            : renderDesktopLayout()}
        </DialogContent>
        <DialogActions
          sx={{
            bgcolor: "#170b3b",
            borderTop: "1px solid #120a3a",
            p: 1.5,
          }}
        >
          <Button
            onClick={handleClose}
            disabled={saving || walletConnecting}
            sx={{
              fontWeight: 600,
              color: "#e2d5ff",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Default Wallet Confirmation Modal */}
      <Dialog
        open={defaultWalletDialogOpen}
        onClose={handleDismissDefaultWallet}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: 18,
            bgcolor: "#12062a",
            color: "#ffd24d",
          }}
        >
          Set Default Abstract Wallet?
        </DialogTitle>
        <DialogContent
          sx={{
            bgcolor: "#05010f",
            pt: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: "#e2d5ff", mb: 1 }}>
            Do you want to use this wallet as your{" "}
            <strong>Default Abstract Wallet</strong> for wagers, rewards, and
            payouts?
          </Typography>
          {pendingDefaultWallet && (
            <Box
              sx={{
                mt: 1,
                p: 1,
                borderRadius: 1.5,
                border: "1px solid rgba(255,255,255,0.18)",
                bgcolor: "rgba(111,82,237,0.1)",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#9aa0ff", textTransform: "uppercase" }}
              >
                Wallet
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#e2d5ff", wordBreak: "break-all" }}
              >
                {pendingDefaultWallet}
              </Typography>
            </Box>
          )}

          <Typography
            variant="caption"
            sx={{ color: "#9aa0ff", display: "block", mt: 1.5 }}
          >
            You can change your default wallet anytime from Settings → Integrations.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{ bgcolor: "#05010f", borderTop: "1px solid #120a3a" }}
        >
          <Button
            onClick={handleDismissDefaultWallet}
            disabled={saving}
            sx={{ color: "#e2d5ff" }}
          >
            Not now
          </Button>
          <Button
            onClick={handleConfirmDefaultWallet}
            disabled={saving}
            variant="contained"
            sx={{
              fontWeight: 700,
              bgcolor: "#6f52ed",
              "&:hover": { bgcolor: "#5a3ed3" },
            }}
          >
            {saving ? "Saving…" : "Yes, set as default"}
          </Button>
        </DialogActions>
      </Dialog>

      <ClaimPhysicalCardModal
        open={claimPhysicalCardOpen}
        onClose={() => setClaimPhysicalCardOpen(false)}
      />
    </>
  );
};

export default AccountSettingsModal;
